import React from "react";
import Loading from "../Loading";
import { formatDate } from "./formatDate";

const USERS_PER_PAGE = 10;

export default function DashboardUsersModal({
  title,
  isOpen,
  onClose,
  users = [],
  usersPerPage = USERS_PER_PAGE,
  currentPage,
  onPageChange,
  totalCount = null,
  loading = false,
  headerExtra = null,
}) {
  if (!isOpen) return null;

  const isServerPagination = totalCount !== null && totalCount !== undefined;
  const total = isServerPagination ? totalCount : users.length;
  const totalPages = Math.ceil(total / usersPerPage) || 1;
  const start = (currentPage - 1) * usersPerPage;
  const paginatedUsers = isServerPagination
    ? users
    : users.slice(start, start + usersPerPage);
  const from = total > 0 ? start + 1 : 0;
  const to = Math.min(start + usersPerPage, total);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3 sm:p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dashboard-users-modal-title">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col my-4">
        <div className="p-4 sm:p-6 flex-shrink-0 border-b border-gray-200">
          <div className="flex items-start justify-between gap-4">
            <h2
              id="dashboard-users-modal-title"
              className="text-lg sm:text-xl md:text-2xl font-bold text-black font-main">
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 text-lg sm:text-xl font-bold rounded-full hover:bg-gray-100 flex-shrink-0"
              aria-label="Close">
              ✕
            </button>
          </div>
          {headerExtra && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              {headerExtra}
            </div>
          )}
        </div>

        <div className="flex-1 flex flex-col min-h-0 overflow-auto p-4 mt-4 sm:p-6 pt-0 sm:pt-0 min-w-0">
          {loading ? (
            <div className="flex-1 flex justify-center items-center min-h-[200px]">
              <Loading variant="modal" />
            </div>
          ) : users.length > 0 ? (
            <>
              <div className="overflow-x-auto rounded-lg border border-gray-200 min-w-0">
                <table className="w-full min-w-[640px] border-collapse text-xs sm:text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border-b border-gray-200 px-2 sm:px-3 py-2.5 text-left font-semibold text-gray-700 whitespace-nowrap">
                        First Name
                      </th>
                      <th className="border-b border-gray-200 px-2 sm:px-3 py-2.5 text-left font-semibold text-gray-700 whitespace-nowrap">
                        Last Name
                      </th>
                      <th className="border-b border-gray-200 px-2 sm:px-3 py-2.5 text-left font-semibold text-gray-700 whitespace-nowrap">
                        Email
                      </th>
                      <th className="border-b border-gray-200 px-2 sm:px-3 py-2.5 text-left font-semibold text-gray-700 whitespace-nowrap">
                        Contact
                      </th>
                      <th className="border-b border-gray-200 px-2 sm:px-3 py-2.5 text-left font-semibold text-gray-700 whitespace-nowrap">
                        Gender
                      </th>
                      <th className="border-b border-gray-200 px-2 sm:px-3 py-2.5 text-left font-semibold text-gray-700 whitespace-nowrap">
                        Created Date & Time
                      </th>
                      <th className="border-b border-gray-200 px-2 sm:px-3 py-2.5 text-left font-semibold text-gray-700 whitespace-nowrap">
                        Document
                      </th>
                      <th className="border-b border-gray-200 px-2 sm:px-3 py-2.5 text-center font-semibold text-gray-700 whitespace-nowrap">
                        Source
                      </th>
                      <th className="border-b border-gray-200 px-2 sm:px-3 py-2.5 text-left font-semibold text-gray-700 whitespace-nowrap">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers.map((user, index) => (
                      <tr
                        key={user.id || index}
                        className="hover:bg-gray-50 border-b border-gray-100 last:border-0">
                        <td className="px-2 sm:px-3 py-2.5 text-gray-700 font-main whitespace-nowrap">
                          {user.firstName ?? "—"}
                        </td>
                        <td className="px-2 sm:px-3 py-2.5 text-gray-700 font-main whitespace-nowrap">
                          {user.lastName ?? "—"}
                        </td>
                        <td className="px-2 sm:px-3 py-2.5 text-gray-700 font-main max-w-[140px] sm:max-w-[200px] truncate">
                          {user.email ?? "—"}
                        </td>
                        <td className="px-2 sm:px-3 py-2.5 text-gray-700 font-main whitespace-nowrap">
                          {user.contactNo || "—"}
                        </td>
                        <td className="px-2 sm:px-3 py-2.5 text-gray-700 font-main whitespace-nowrap">
                          {user.gender ?? "—"}
                        </td>
                        <td className="px-2 sm:px-3 py-2.5 text-gray-700 font-main whitespace-nowrap">
                          {formatDate(user.createdDate)}
                        </td>
                        <td
                          className={`px-2 sm:px-3 py-2.5 font-main font-semibold whitespace-nowrap ${
                            user.document === "Not Uploaded"
                              ? "text-red-500"
                              : "text-green-500"
                          }`}>
                          {user.document || "Not Uploaded"}
                        </td>
                        <td className="px-2 sm:px-3 py-2.5 text-center">
                          <span
                            className={`inline-flex px-3 py-1 text-xs leading-5 font-semibold rounded-full border ${
                              user.is_web === 1
                                ? "bg-blue-100 text-blue-800 border-blue-200"
                                : "bg-green-100 text-green-800 border-green-200"
                            }`}>
                            {user.is_web === 1 ? "Web" : "Mobile"}
                          </span>
                        </td>
                        <td className="px-2 sm:px-3 py-2.5 font-main whitespace-nowrap">
                          {user.isActive === 1 ? (
                            <span className="text-green-600 font-medium">Active</span>
                          ) : user.isActive === 0 ? (
                            <span className="text-gray-500">Inactive</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination - same pattern as Boost History */}
              {totalPages > 1 && (
                <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 px-3 sm:px-4 py-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-xs sm:text-sm font-medium text-gray-700 order-2 sm:order-1 text-center sm:text-left font-main">
                    Page <span className="font-bold text-gray-900">{currentPage}</span> of{" "}
                    <span className="font-bold text-gray-900">{totalPages}</span> · Total{" "}
                    <span className="font-bold text-red-600">{total}</span> records
                  </div>
                  <div className="flex gap-2 items-center order-1 sm:order-2">
                    <button
                      type="button"
                      onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className={`px-3 sm:px-4 py-2 border rounded-lg font-medium text-xs sm:text-sm transition-colors font-main ${
                        currentPage === 1
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                          : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                      }`}>
                      Previous
                    </button>
                    <span className="text-xs sm:text-sm text-gray-700 min-w-[100px] sm:min-w-[120px] text-center font-main">
                      <span className="font-bold">{from}</span>–<span className="font-bold">{to}</span> of{" "}
                      <span className="font-bold text-red-600">{total}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className={`px-3 sm:px-4 py-2 border rounded-lg font-medium text-xs sm:text-sm transition-colors font-main ${
                        currentPage === totalPages
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                          : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                      }`}>
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-gray-500 text-center py-8 font-main text-sm">
              No users found
            </p>
          )}
        </div>

        <div className="p-4 sm:p-6 pt-0 flex-shrink-0 border-t border-gray-100 flex justify-end">
          <button
            type="button"
            className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 font-main font-semibold text-sm"
            onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
